import { CheckCircleIcon, SendIcon, UsersIcon, XCircleIcon } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Spinner } from "@/components/ui/spinner"
import { toast } from "@/components/ui/toast"
import {
  guildWarApi,
  type UnsignedMember,
  type UnsignedMemberListResult,
  type ReminderSendResult,
} from "@/lib/guild-war-api"

type Phase = "idle" | "loading" | "loaded" | "sending" | "sent" | "error"

export function ReminderActionCard() {
  const [phase, setPhase] = useState<Phase>("idle")
  const [unsignedData, setUnsignedData] =
    useState<UnsignedMemberListResult | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sendResult, setSendResult] = useState<ReminderSendResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const handleLoadUnsigned = useCallback(async () => {
    setPhase("loading")
    setErrorMsg(null)
    setSendResult(null)
    try {
      const data = await guildWarApi.getUnsignedMembers()
      setUnsignedData(data)
      setSelectedIds(new Set(data.members.map((m) => m.memberId)))
      setPhase("loaded")
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : "Failed to load unsigned members",
      )
      setPhase("error")
    }
  }, [])

  const handleSend = useCallback(async () => {
    if (!unsignedData || selectedIds.size === 0) return

    setPhase("sending")
    setErrorMsg(null)
    try {
      const result = await guildWarApi.sendReminders({
        memberIds: [...selectedIds],
        weekId: unsignedData.weekId,
      })
      setSendResult(result)
      setPhase("sent")
      if (result.failedCount > 0) {
        toast.info(
          `Sent ${result.sentCount} reminders, ${result.failedCount} failed`,
        )
      } else {
        toast.success(`Successfully sent ${result.sentCount} reminders`)
      }
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : "Failed to send reminders",
      )
      setPhase("error")
    }
  }, [unsignedData, selectedIds])

  const toggleMember = useCallback((memberId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(memberId)) {
        next.delete(memberId)
      } else {
        next.add(memberId)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    if (!unsignedData) return
    setSelectedIds(new Set(unsignedData.members.map((m) => m.memberId)))
  }, [unsignedData])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const allSelected = useMemo(
    () =>
      unsignedData !== null &&
      unsignedData.members.length > 0 &&
      selectedIds.size === unsignedData.members.length,
    [unsignedData, selectedIds],
  )

  return (
    <Card variant="elevated">
      <CardHeader>
        <CardHeading
          icon={<SendIcon className="size-5 text-primary" />}
          className="text-xl"
        >
          Remind Unsigned Members
        </CardHeading>
        <CardDescription>
          Send DM reminders to members who haven't signed up for the current
          guild war week.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {phase === "idle" && (
          <Button onClick={handleLoadUnsigned} variant="themed">
            <UsersIcon className="size-4" />
            Load Unsigned Members
          </Button>
        )}

        {phase === "loading" && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner className="size-4" />
            Loading unsigned members...
          </div>
        )}

        {phase === "error" && errorMsg && (
          <StatusAlert tone="danger">
            {errorMsg}
            <Button
              variant="ghost"
              size="sm"
              className="ml-2"
              onClick={handleLoadUnsigned}
            >
              Retry
            </Button>
          </StatusAlert>
        )}

        {(phase === "loaded" || phase === "sending") && unsignedData && (
          <>
            <div className="text-sm text-muted-foreground">
              Week:{" "}
              <span className="font-medium text-foreground">
                {unsignedData.weekendLabel}
              </span>{" "}
              &middot;{" "}
              {unsignedData.members.length === 0
                ? "No unsigned members"
                : `${unsignedData.members.length} unsigned member${unsignedData.members.length !== 1 ? "s" : ""}`}
            </div>

            {unsignedData.members.length === 0 && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-center text-sm text-muted-foreground">
                All members have signed up for this week.
              </div>
            )}

            {unsignedData.members.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={selectAll}
                    disabled={allSelected}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={deselectAll}
                    disabled={selectedIds.size === 0}
                  >
                    Deselect All
                  </Button>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {selectedIds.size} of {unsignedData.members.length} selected
                  </span>
                </div>

                <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border/40 p-2">
                  {unsignedData.members.map((member) => (
                    <MemberRow
                      key={member.memberId}
                      member={member}
                      checked={selectedIds.has(member.memberId)}
                      onToggle={() => toggleMember(member.memberId)}
                      disabled={phase === "sending"}
                    />
                  ))}
                </div>

                <div className="pt-1">
                  <SendConfirmButton
                    selectedCount={selectedIds.size}
                    weekendLabel={unsignedData.weekendLabel}
                    onConfirm={handleSend}
                    disabled={selectedIds.size === 0 || phase === "sending"}
                    loading={phase === "sending"}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {phase === "sent" && sendResult && (
          <>
            <div className="flex items-center gap-2 text-sm font-medium text-success">
              <CheckCircleIcon className="size-4" />
              Reminders sent for {sendResult.weekendLabel}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-border/40 bg-muted/30 p-3">
                <div className="text-muted-foreground">Sent</div>
                <div className="text-lg font-semibold">
                  {sendResult.sentCount}
                </div>
              </div>
              <div className="rounded-lg border border-border/40 bg-muted/30 p-3">
                <div className="text-muted-foreground">Failed</div>
                <div className="text-lg font-semibold">
                  {sendResult.failedCount}
                </div>
              </div>
            </div>
            {sendResult.failedCount > 0 &&
              sendResult.targetDisplayNames.length > 0 && (
                <StatusAlert tone="danger">
                  <div className="flex items-center gap-1.5 font-medium">
                    <XCircleIcon className="size-3.5" />
                    Failed deliveries
                  </div>
                  <p className="mt-1 text-danger/80">
                    {sendResult.targetDisplayNames.join(", ")}
                  </p>
                </StatusAlert>
              )}
            <Button variant="outline" onClick={handleLoadUnsigned}>
              Reload
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function MemberRow({
  member,
  checked,
  onToggle,
  disabled,
}: {
  member: UnsignedMember
  checked: boolean
  onToggle: () => void
  disabled: boolean
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50">
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
      />
      <span className="truncate">{member.displayName}</span>
    </label>
  )
}

function SendConfirmButton({
  selectedCount,
  weekendLabel,
  onConfirm,
  disabled,
  loading,
}: {
  selectedCount: number
  weekendLabel: string
  onConfirm: () => void
  disabled: boolean
  loading: boolean
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button variant="default" disabled={disabled}>
            {loading && <Spinner className="size-4" />}
            Send Reminders ({selectedCount})
          </Button>
        }
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Send DM Reminders?</AlertDialogTitle>
          <AlertDialogDescription>
            This will send a direct message to{" "}
            <span className="font-semibold text-foreground">
              {selectedCount}
            </span>{" "}
            member{selectedCount !== 1 ? "s" : ""} for{" "}
            <span className="font-semibold text-foreground">
              {weekendLabel}
            </span>
            .
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            Send Reminders
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
